/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { BigModal } from './BigModal';
import { Typography, Grid, Tabs, Tab, Box } from '@mui/material';
import {
  CustomTextField,
  SuccessModal,
  InspectEmpDetails,
  SaveNewEmpModal,
  ContainedButton,
} from '../index';
import { apiUrl } from '../../utilities/apiUrl';
import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ModalButton } from './ModalButton';
import { useAlert } from '../../providers/AlertContext';

export const CreateEditRequestModal = (props) => {
  const inspectRef = useRef();
  const [empId] = useOutletContext();
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [middlename, setMiddleName] = useState('');
  const [email, setEmail] = useState('');
  const [editSuccessOpen, setEditSuccessOpen] = useState(false);
  const [tabValue, setTabValue] = useState('personalInfo');
  const [createEditRequestModal, setCreateEditRequestModal] = useState(false);
  const { showAlert } = useAlert();

  const handleClose = () => {
    props.onClose();
  };

  useEffect(() => {
    if (!props.empData) return;
    const empdata = props.empData;
    setLastname(empdata.lastname);
    setFirstname(empdata.firstname);
    setMiddleName(empdata.middlename);
    setEmail(empdata.email);
  }, [props.empData]);

  const handleEdit = async () => {
    // Gets all the data in InspectEmpDetails component
    const inspectData = inspectRef.current?.getAllData?.();

    fetch(apiUrl('/eis/empeditrequest/edit-request'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empid: empId,
        proposeddata: {
          basicinfo: {
            firstname: firstname,
            lastname: lastname,
            email: email,
            middlename: middlename,
          },
          personalinfo: {
            nickname: inspectData.emp.nickname,
            age: inspectData.emp.age,
            sex: inspectData.emp.sex,
            dateofbirth: inspectData.emp.dateofbirth,
            placeofbirth: inspectData.emp.placeofbirth,
            bloodtype: inspectData.emp.bloodtype,
            civilstatus: inspectData.emp.civilstatus,
            citizenship: inspectData.emp.citizenship,
            height: inspectData.emp.height,
            weight: inspectData.emp.weight,
            cellphone: inspectData.emp.cellphone,
            passport: inspectData.emp.passport,
            sss: inspectData.emp.sss,
            pagibig: inspectData.emp.pagibig,
            philhealth: inspectData.emp.philhealth,
            gsis: inspectData.emp.gsis,
            tin: inspectData.emp.tin,
            skills: inspectData.emp.skills,
            hobbies: inspectData.emp.hobbies,
          },
          empaddress: inspectData.empAddress,
          emprelations: inspectData.empRelations,
          empeducation: inspectData.empEducation,
          empcivservice: inspectData.empCivService,
          empworkexp: inspectData.empWorkExp,
          empcivicwork: inspectData.empVolWork,
          emptraining: inspectData.emptrn,
          emphistory: inspectData.empHistory,
        },
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setEditSuccessOpen(true);
        } else {
          showAlert(data.message, 'warning', 'Invalid Form Inputs');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleTabChange = (event, value) => {
    setTabValue(value);
  };

  const handleSuccessClose = () => {
    props.frontendsetter();
    setEditSuccessOpen(false);
    handleClose();
  };

  return (
    <>
      <BigModal
        style={{ display: 'flex', flexDirection: 'column' }}
        width={'80%'}
        open={props.open}
        onClose={handleClose}
      >
        <Typography sx={{ mb: 1, fontWeight: 700 }} variant="h4">
          Create Edit Request
        </Typography>

        <Grid container columnSpacing={4}>
          <Grid item xs={6}>
            <CustomTextField
              error={!firstname}
              errorMsg={'Please enter your first name'}
              required={true}
              label="First Name"
              value={firstname}
              onChange={(event) => {
                setFirstname(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextField
              error={!lastname}
              errorMsg={'Please enter your last name'}
              required={true}
              label="Last Name"
              value={lastname}
              onChange={(event) => {
                setLastname(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextField
              required={false}
              label="Middle Name"
              value={middlename}
              onChange={(event) => {
                setMiddleName(event.target.value);
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <CustomTextField
              error={!email}
              errorMsg={'Please enter a valid email'}
              required={true}
              label="Email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            width: '100%',
            overflowY: 'auto',
          }}
        >
          {/* DONT FORGET TO PASS tabValue as a prop */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="personalInfo" label="Personal Information" />
            <Tab value="addresses" label="Addresses" />
            <Tab
              value="familyBackground"
              label="Family Background & Relations"
            />
            <Tab value="educBackground" label="Educational Background" />
            <Tab value="civService" label="Civil Service Eligibility" />
            <Tab value="workExp" label="Work Experience" />
            <Tab value="volWork" label="Voluntary Work" />
            <Tab value="trainingProgs" label="Training Programs" />
            <Tab value="history" label="History" />
          </Tabs>

          <Box sx={{}}>
            <InspectEmpDetails
              ref={inspectRef}
              currentTab={tabValue}
              empid={props.empid}
            />
          </Box>
        </Box>

        <ModalButton
          primaryBtnName="Save"
          secondaryBtnName="Cancel"
          primaryClickHandler={() => {
            setCreateEditRequestModal(true);
          }}
          secondaryClickHandler={() => {
            props.onClose();
          }}
          // Ensures all fields are properly filled before submit button becomes available
          disabled={!firstname || !lastname || !email}
        />
      </BigModal>

      <SuccessModal
        open={editSuccessOpen}
        onClose={() => {
          setEditSuccessOpen(false);
          handleSuccessClose();
        }}
        close={handleSuccessClose}
        desc={`You have created an edit request for your profile`}
      />

      <SaveNewEmpModal
        handleEdit={handleEdit}
        open={createEditRequestModal}
        onClose={() => {
          setCreateEditRequestModal(false);
        }}
        savedName={`${firstname} ${lastname}`}
      />
    </>
  );
};
